// LineSummaryView — the period_over_period LINE view.
//
// Draws TWO lines aligned by their position within each period — the current
// period (solid `#8884d8`) and the prior period (dashed gray) — and fills the
// area BETWEEN them: green where the current line sits above prior, red where
// it sits below. The fill is split exactly at crossover points so the colors
// meet where the lines cross, not at the next data vertex.
//
// Both series come from the raw feature timeseries the CARD supplies via the
// `timeseries` prop (it owns the `GET /api/features/timeseries/{feature_id}`
// fetch — see SUMMARY_VIEW_SOURCES). This view just slices that series to the
// current and prior period bounds carried in the numeric payload
// (`current_period` / `prior_period`). No fetch, no backend change.

import React, { useMemo } from 'react'
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts'
import type { SummaryViewBodyProps } from '../viewRegistry'
import { periodLabels } from '../periodFormat'
import { isoToLocalMs } from '../../../charts/windowRange'
import { numericExtent, numericTickFormatter, numericYAxisWidth } from '../../../charts/axisWidth'
import { formatDateLabel } from '../../../charts/timeAxis'

const DAY_MS = 86_400_000

/** A point on one period's series: `x` is the integer day-offset from that
 *  period's start, `v` the (mean) value on that day. */
export interface OffsetPoint {
  x: number
  v: number
}

/** Which direction counts as progress (green) vs regression (red). `higher` =
 *  bigger is better, `lower` = smaller is better, `none` = no good/bad (the
 *  whole band is painted one neutral color). */
export type ProgressDirection = 'higher' | 'lower' | 'none'

/** A row of the aligned comparison series fed to the chart. `good`/`bad`/
 *  `neutral` are the vertical bands to fill between the two lines (`[low,
 *  high]`); exactly one is present per segment depending on the configured
 *  progress direction and which line is on top. */
export interface ComparisonRow {
  x: number
  cur: number | null
  prior: number | null
  good?: [number, number]
  bad?: [number, number]
  neutral?: [number, number]
}

/** How multiple check-ins on the same day collapse into one plotted point. */
export type SameDayAgg = 'sum' | 'mean'

/** Group raw in-period points by integer day-offset, combining same-day values
 *  by `mode` (sum by default), returned sorted ascending. */
export function toOffsetPoints(
  points: Array<{ date: number; value: number }>,
  periodStartMs: number,
  mode: SameDayAgg = 'sum',
): OffsetPoint[] {
  const byOffset = new Map<number, { sum: number; n: number }>()
  for (const p of points) {
    const x = Math.round((p.date - periodStartMs) / DAY_MS)
    const acc = byOffset.get(x) ?? { sum: 0, n: 0 }
    acc.sum += p.value
    acc.n += 1
    byOffset.set(x, acc)
  }
  return Array.from(byOffset.entries())
    .map(([x, { sum, n }]) => ({ x, v: mode === 'mean' ? sum / n : sum }))
    .sort((a, b) => a.x - b.x)
}

/** Linearly interpolate a series at x, or null when x is outside its span. */
function interp(pts: OffsetPoint[], x: number): number | null {
  if (pts.length === 0) return null
  if (x < pts[0].x || x > pts[pts.length - 1].x) return null
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]
    const b = pts[i + 1]
    if (x >= a.x && x <= b.x) {
      if (b.x === a.x) return a.v
      const t = (x - a.x) / (b.x - a.x)
      return a.v + t * (b.v - a.v)
    }
  }
  return pts[pts.length - 1].v
}

/** Build the aligned current-vs-prior series with signed fill bands.
 *
 *  Both series are sampled on the union of their day-offsets (so each polyline
 *  is represented exactly), a synthetic vertex is inserted wherever the two
 *  cross, and each vertex carries the good/bad (or neutral) band between the
 *  lines. `direction` decides which side of a crossing is "good" (green):
 *  `higher` → current-above-prior is good; `lower` → current-below is good;
 *  `none` → no good/bad, the whole band is neutral. */
export function buildComparisonSeries(
  cur: OffsetPoint[],
  prior: OffsetPoint[],
  direction: ProgressDirection = 'higher',
): ComparisonRow[] {
  if (cur.length === 0) return []

  const xs = Array.from(
    new Set<number>([...cur.map((p) => p.x), ...prior.map((p) => p.x)]),
  ).sort((a, b) => a - b)

  const base: ComparisonRow[] = xs.map((x) => ({
    x,
    cur: interp(cur, x),
    prior: interp(prior, x),
  }))

  // Insert crossover vertices where both series are defined and the sign of
  // (cur − prior) flips between consecutive vertices.
  const rows: ComparisonRow[] = []
  for (let i = 0; i < base.length; i++) {
    rows.push(base[i])
    const a = base[i]
    const b = base[i + 1]
    if (b && a.cur != null && a.prior != null && b.cur != null && b.prior != null) {
      const da = a.cur - a.prior
      const db = b.cur - b.prior
      if ((da > 0 && db < 0) || (da < 0 && db > 0)) {
        const t = da / (da - db)
        const x = a.x + t * (b.x - a.x)
        const v = a.cur + t * (b.cur - a.cur)
        rows.push({ x, cur: v, prior: v })
      }
    }
  }

  // Assign the fill band at each vertex. `none` paints everything neutral.
  // Otherwise the band is good (green) or bad (red) by whether the current line
  // is on its "better" side. At a crossover both lines are equal → a zero-height
  // band on BOTH good/bad so each polygon closes cleanly at the crossing.
  for (const r of rows) {
    if (r.cur == null || r.prior == null) continue
    const lo = Math.min(r.cur, r.prior)
    const hi = Math.max(r.cur, r.prior)
    if (direction === 'none') {
      r.neutral = [lo, hi]
    } else if (r.cur === r.prior) {
      r.good = [r.cur, r.cur]
      r.bad = [r.cur, r.cur]
    } else {
      const currentAbove = r.cur > r.prior
      const isGood = direction === 'higher' ? currentAbove : !currentAbove
      if (isGood) r.good = [lo, hi]
      else r.bad = [lo, hi]
    }
  }
  return rows
}

const Shell: React.FC<{
  currentCount: number
  priorCount: number
  children: React.ReactNode
}> = ({ currentCount, priorCount, children }) => (
  <div
    data-testid="summary-line-view"
    data-point-count={currentCount}
    data-prior-count={priorCount}
  >
    {children}
  </div>
)

const LineSummaryView: React.FC<SummaryViewBodyProps> = ({ numeric, unit, granularity, config, timeseries = [] }) => {
  const payload = numeric.numeric_payload ?? {}
  const featureId: number | undefined =
    typeof payload.feature_id === 'number' ? payload.feature_id : undefined

  // Same-day check-ins collapse to one daily point: sum by default, mean when
  // the summary config opts in (`params.same_day_agg === 'mean'`).
  const sameDayAgg: SameDayAgg = config?.params?.same_day_agg === 'mean' ? 'mean' : 'sum'

  // Which way is progress (green) vs regression (red). Defaults to higher-is-
  // better; `none` paints the whole fill neutral.
  const pd = config?.params?.progress_direction
  const direction: ProgressDirection = pd === 'lower' ? 'lower' : pd === 'none' ? 'none' : 'higher'

  // Descriptive labels shared with the stats table: the current period spelled
  // out ("week starting May 24"), the prior as "previous week/month/year".
  const labels =
    payload?.current_period && payload?.prior_period
      ? periodLabels(payload.current_period, payload.prior_period, granularity)
      : { current: 'this period', prior: 'previous period' }

  const currentPeriod = useMemo(() => {
    const cp = payload?.current_period
    if (!cp || typeof cp.start !== 'string' || typeof cp.end !== 'string') return null
    return { start: isoToLocalMs(cp.start), end: isoToLocalMs(cp.end) }
  }, [payload?.current_period])

  const priorPeriod = useMemo(() => {
    const pp = payload?.prior_period
    if (!pp || typeof pp.start !== 'string' || typeof pp.end !== 'string') return null
    return { start: isoToLocalMs(pp.start), end: isoToLocalMs(pp.end) }
  }, [payload?.prior_period])

  // Raw points sliced to each period (half-open [start, end)).
  const curPoints = useMemo(() => {
    if (!currentPeriod) return []
    return timeseries.filter((p) => p.date >= currentPeriod.start && p.date < currentPeriod.end)
  }, [timeseries, currentPeriod])

  const priorPoints = useMemo(() => {
    if (!priorPeriod) return []
    return timeseries.filter((p) => p.date >= priorPeriod.start && p.date < priorPeriod.end)
  }, [timeseries, priorPeriod])

  // Aligned comparison series (offsets → bands) keyed off the current period's
  // start, so the x-axis reads as current-period dates.
  const series = useMemo(() => {
    if (!currentPeriod) return []
    const cur = toOffsetPoints(curPoints, currentPeriod.start, sameDayAgg)
    const prior = priorPeriod ? toOffsetPoints(priorPoints, priorPeriod.start, sameDayAgg) : []
    return buildComparisonSeries(cur, prior, direction)
  }, [curPoints, priorPoints, currentPeriod, priorPeriod, sameDayAgg, direction])

  // Size the Y axis to its widest value label (over both current and prior).
  const yAxisWidth = useMemo(() => {
    const [lo, hi] = numericExtent(series.flatMap((r) => [r.cur, r.prior]))
    return numericYAxisWidth(lo, hi, { fontSizePx: 15 })
  }, [series])

  // Which fill bands actually carry data — recharts throws if an Area is
  // rendered with an all-undefined range dataKey, so render only the present
  // ones (direction-agnostic: a 'higher' chart with current always below prior
  // has only a 'bad' band, etc.).
  const bands = useMemo(() => {
    let good = false, bad = false, neutral = false
    for (const r of series) {
      if (r.good) good = true
      if (r.bad) bad = true
      if (r.neutral) neutral = true
    }
    return { good, bad, neutral }
  }, [series])

  const hasPrior = priorPoints.length > 0
  const currentStartMs = currentPeriod?.start ?? 0
  const priorStartMs = priorPeriod?.start ?? 0
  const offsetToCurrentDate = (x: number) => currentStartMs + Math.round(x) * DAY_MS

  if (featureId === undefined) {
    return (
      <Shell currentCount={0} priorCount={0}>
        <div className="flex h-64 items-center justify-center text-sm text-gray-500">
          <p>No feature to chart.</p>
        </div>
      </Shell>
    )
  }

  if (curPoints.length === 0) {
    return (
      <Shell currentCount={0} priorCount={priorPoints.length}>
        <div className="flex h-64 items-center justify-center text-sm text-gray-500">
          <p>No values in this period.</p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell currentCount={curPoints.length} priorCount={priorPoints.length}>
      {/* Legend: solid = current period, dashed = prior period. */}
      <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 px-1 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-4 rounded bg-[#8884d8]" />
          {labels.current}
        </span>
        {hasPrior && (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-0 w-4 border-t-2 border-dashed border-gray-400" />
            {labels.prior}
          </span>
        )}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 25 }}>
            <XAxis
              dataKey="x"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(x: number) => formatDateLabel(offsetToCurrentDate(x), false)}
              angle={-45}
              textAnchor="end"
              height={50}
              tick={{ fontSize: 15 }}
              tickLine={false}
            />
            <YAxis tick={{ fontSize: 15 }} width={yAxisWidth} tickFormatter={numericTickFormatter} domain={['dataMin', 'dataMax']} tickLine={false} />
            <Tooltip
              content={({ active, payload: tp }) => {
                if (!active || !tp || tp.length === 0) return null
                const row = tp[0].payload as ComparisonRow
                if (row.cur == null && row.prior == null) return null
                const fmt = (v: number | null) =>
                  v == null ? '—' : `${Math.round(v * 100) / 100}${unit ? ` ${unit}` : ''}`
                // Label each value by its own real date (current vs the aligned
                // prior date). Date formatting clones LineChartBody's tooltip:
                // weekday + M/D, with the year only when it's ambiguous — here,
                // when the current and prior periods fall in different years.
                const curDate = offsetToCurrentDate(row.x)
                const priorDate = priorStartMs + Math.round(row.x) * DAY_MS
                const withYear =
                  hasPrior &&
                  new Date(curDate).getFullYear() !== new Date(priorDate).getFullYear()
                return (
                  <div className="rounded border border-gray-200 bg-white px-2 py-1 text-sm shadow-md">
                    <div className="text-[#6b62d6]">
                      {formatDateLabel(curDate, withYear)}: {fmt(row.cur)}
                    </div>
                    {hasPrior && row.prior != null && (
                      <div className="text-gray-500">
                        {formatDateLabel(priorDate, withYear)}: {fmt(row.prior)}
                      </div>
                    )}
                  </div>
                )
              }}
            />
            {/* Fill bands between the two lines (behind the lines): green for
                progress, red for regression — or one neutral gray when the
                direction is "neither". Only bands that carry data are rendered
                (separate conditional children, not a fragment, so recharts still
                sees them). */}
            {bands.good && (
              <Area
                dataKey="good"
                type="monotone"
                stroke="none"
                fill="#16a34a"
                fillOpacity={0.22}
                connectNulls={false}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            {bands.bad && (
              <Area
                dataKey="bad"
                type="monotone"
                stroke="none"
                fill="#dc2626"
                fillOpacity={0.22}
                connectNulls={false}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            {bands.neutral && (
              <Area
                dataKey="neutral"
                type="monotone"
                stroke="none"
                fill="#94a3b8"
                fillOpacity={0.25}
                connectNulls={false}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            {/* Prior period — dashed gray, drawn under the current line. */}
            {hasPrior && (
              <Line
                dataKey="prior"
                type="monotone"
                stroke="#9ca3af"
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            )}
            {/* Current period — solid. */}
            <Line
              dataKey="cur"
              type="monotone"
              stroke="#8884d8"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {!hasPrior && (
        <p className="mt-1 px-1 text-xs text-gray-400">No prior-period data to compare.</p>
      )}
    </Shell>
  )
}

export default LineSummaryView
